import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import PersonAddAltRoundedIcon from "@mui/icons-material/PersonAddAltRounded";
import LockResetRoundedIcon from "@mui/icons-material/LockResetRounded";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext.tsx";
import { supabase } from "../supabaseClient.ts";

export function LoginPage() {
  const {
    configured,
    user,
    role,
    loading,
    signIn,
  } = useAuth();

  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const [resetMode, setResetMode] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  if (loading) {
    return (
      <Box sx={{ p: 6, textAlign: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (user && role) {
    return (
      <Navigate
        to={role === "admin" ? "/beheer" : "/mijn-pippaya"}
        replace
      />
    );
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();

    setBusy(true);
    setError("");

    const err = await signIn(
      email.trim(),
      password
    );

    setBusy(false);

    if (err) {
      setError(
        "Inloggen is niet gelukt. Controleer je e-mailadres en wachtwoord."
      );
    } else {
      navigate(
        (location.state as any)?.from || "/mijn-pippaya",
        { replace: true }
      );
    }
  };

  const sendPasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setResetSent(false);

    const cleanEmail = email.trim();

    if (!configured || !supabase) {
      setError("Wachtwoordherstel is tijdelijk niet beschikbaar.");
      return;
    }

    if (!cleanEmail) {
      setError("Vul je e-mailadres in.");
      return;
    }

    setResetBusy(true);

    const redirectTo = `${window.location.origin}/account-activeren`;

    const { error: resetError } =
      await supabase.auth.resetPasswordForEmail(
        cleanEmail,
        { redirectTo }
      );

    setResetBusy(false);

    if (resetError) {
      setError(
        "De herstelmail kon niet worden verstuurd. Probeer het later opnieuw of neem contact op met Pippaya."
      );
      return;
    }

    setResetSent(true);
  };

  const openReset = () => {
    setError("");
    setResetSent(false);
    setPassword("");
    setResetMode(true);
  };

  const backToLogin = () => {
    setError("");
    setResetSent(false);
    setResetMode(false);
  };

  return (
    <Box
      sx={{
        minHeight: "70vh",
        display: "grid",
        placeItems: "center",
        p: 2,
        bgcolor: "#fffafb",
      }}
    >
      <Card
        sx={{
          width: "100%",
          maxWidth: 480,
          borderRadius: 4,
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h4">
            {resetMode
              ? "Wachtwoord herstellen"
              : "Mijn Pippaya"}
          </Typography>

          <Typography
            sx={{ mt: 1, mb: 3 }}
            color="text.secondary"
          >
            {resetMode
              ? "Vul het e-mailadres in waarmee je Mijn Pippaya gebruikt. Je ontvangt een e-mail waarmee je een nieuw wachtwoord kunt instellen."
              : "Log in om je honden en reserveringen te bekijken of nieuwe opvang aan te vragen."}
          </Typography>

          {!configured && (
            <Alert
              severity="warning"
              sx={{ mb: 2 }}
            >
              De Supabase-koppeling moet nog in de
              hostinginstellingen worden ingevuld.
            </Alert>
          )}

          {error && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
            >
              {error}
            </Alert>
          )}

          {resetMode ? (
            <Stack spacing={2}>
              {resetSent && (
                <Alert severity="success">
                  Als dit e-mailadres bij Mijn Pippaya bekend is,
                  ontvang je binnen enkele minuten een e-mail.
                  Controleer eventueel ook je map met ongewenste e-mail.
                </Alert>
              )}

              {!resetSent && (
                <Box
                  component="form"
                  onSubmit={sendPasswordReset}
                  sx={{ display: "grid", gap: 2 }}
                >
                  <TextField
                    label="E-mailadres"
                    type="email"
                    value={email}
                    onChange={(e) =>
                      setEmail(e.target.value)
                    }
                    autoComplete="email"
                    required
                  />

                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={resetBusy || !configured}
                    startIcon={<LockResetRoundedIcon />}
                  >
                    {resetBusy
                      ? "Herstelmail versturen..."
                      : "Stuur herstelmail"}
                  </Button>
                </Box>
              )}

              <Button
                variant="text"
                onClick={backToLogin}
              >
                Terug naar inloggen
              </Button>
            </Stack>
          ) : (
            <>
              <Box
                component="form"
                onSubmit={submit}
                sx={{ display: "grid", gap: 2 }}
              >
                <TextField
                  label="E-mailadres"
                  type="email"
                  value={email}
                  onChange={(e) =>
                    setEmail(e.target.value)
                  }
                  required
                />

                <TextField
                  label="Wachtwoord"
                  type="password"
                  value={password}
                  onChange={(e) =>
                    setPassword(e.target.value)
                  }
                  required
                />

                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={busy || !configured}
                >
                  {busy ? "Bezig..." : "Inloggen"}
                </Button>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Stack spacing={1.25}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  Komt je hond al bij Pippaya, maar heb je nog geen
                  Mijn Pippaya-account?
                </Typography>

                <Button
                  variant="outlined"
                  startIcon={<PersonAddAltRoundedIcon />}
                  onClick={() =>
                    navigate("/mijn-pippaya/activeren")
                  }
                >
                  Mijn Pippaya activeren
                </Button>

                <Button
                  variant="text"
                  startIcon={<LockResetRoundedIcon />}
                  onClick={openReset}
                >
                  Wachtwoord vergeten?
                </Button>
              </Stack>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
